import { useState, useRef, useEffect } from 'react';
import { ZoomIn, ZoomOut, MapPin, Dna } from 'lucide-react';

const CHROM_LENGTHS = {
  '1': 248956422, '2': 242193529, '3': 198295559, '4': 190214555, '5': 181538259,
  '6': 170805979, '7': 159345973, '8': 145138636, '9': 138394717, '10': 133797422,
  '11': 135086622, '12': 133275309, '13': 114364328, '14': 107043718, '15': 101991189,
  '16': 90338345, '17': 83257441, '18': 80373285, '19': 58617616, '20': 64444167,
  '21': 46709983, '22': 50818468, 'X': 156040895, 'Y': 57227415,
};

function pathColor(val) {
  if (val >= 0.7) return '#ef4444';
  if (val >= 0.4) return '#f59e0b';
  return '#22c55e';
}

function ChromIdeogram({ chrom, variantPos }) {
  const chromLen = CHROM_LENGTHS[chrom] || 83257441;
  const relPos = variantPos / chromLen;
  const width = 320;
  const height = 24;
  const varX = Math.round(relPos * width);

  return (
    <div>
      <div className="text-xs text-gray-500 mb-1 flex items-center gap-1">
        <Dna size={11} />
        Chromosome {chrom} ideogram ({(chromLen / 1e6).toFixed(1)} Mb)
      </div>
      <svg width={width} height={height} className="rounded overflow-hidden">
        <defs>
          <linearGradient id="chromGrad" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor="#94a3b8" />
            <stop offset="15%" stopColor="#64748b" />
            <stop offset="50%" stopColor="#475569" />
            <stop offset="85%" stopColor="#64748b" />
            <stop offset="100%" stopColor="#94a3b8" />
          </linearGradient>
        </defs>
        <rect x={0} y={6} width={width} height={12} rx={6} fill="url(#chromGrad)" />
        <rect x={width * 0.47} y={4} width={width * 0.06} height={16} rx={2} fill="#334155" opacity={0.7} />
        <line x1={varX} y1={0} x2={varX} y2={height} stroke="#f93441" strokeWidth={2} />
        <circle cx={varX} cy={height / 2} r={4} fill="#f93441" />
      </svg>
      <div className="text-xs text-gray-400 mt-0.5">
        Variant at {variantPos.toLocaleString()} bp
      </div>
    </div>
  );
}

function GeneTrackSVG({ variant, zoom, offset }) {
  const W = 560;
  const H = 80;
  const REGION_SIZE = Math.round(20000 / zoom);
  const regionStart = variant.position - REGION_SIZE / 2 + offset;
  const regionEnd = regionStart + REGION_SIZE;

  const toX = (pos) => ((pos - regionStart) / (regionEnd - regionStart)) * W;
  const varX = toX(variant.position);

  return (
    <svg width="100%" height={H} viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="xMidYMid meet" className="rounded-xl overflow-hidden bg-gray-50 border border-gray-200">
      {/* Gene line */}
      <line x1={0} y1={30} x2={W} y2={30} stroke="#94a3b8" strokeWidth={2} />

      {/* Exons */}
      {variant.exons.map((exon) => {
        const x1 = toX(exon.start);
        const x2 = toX(exon.end);
        if (x2 < 0 || x1 > W) return null;
        const rx = Math.max(0, x1);
        const rw = Math.min(W, x2) - rx;
        if (rw <= 0) return null;
        return (
          <g key={exon.number}>
            <rect x={rx} y={16} width={rw} height={28} rx={3} fill="#021f30" opacity={0.8} />
            {rw > 20 && (
              <text x={rx + rw / 2} y={34} textAnchor="middle" fontSize={10} fill="white" fontWeight="600">
                E{exon.number}
              </text>
            )}
          </g>
        );
      })}

      {/* Variant marker */}
      {varX >= 0 && varX <= W && (
        <>
          <line x1={varX} y1={0} x2={varX} y2={H} stroke="#f93441" strokeWidth={2} strokeDasharray="4 2" />
          <circle cx={varX} cy={8} r={6} fill="#f93441" />
          <text x={varX + 8} y={12} fontSize={9} fill="#f93441" fontWeight="700">VAR</text>
        </>
      )}

      {/* Nearby variants */}
      {variant.nearby_variants.map((nv) => {
        const nx = toX(nv.pos);
        if (nx < 0 || nx > W) return null;
        return (
          <circle key={nv.id} cx={nx} cy={65} r={5} fill={pathColor(nv.pathogenicity)} opacity={0.85} title={nv.id} />
        );
      })}

      {/* Region labels */}
      <text x={4} y={H - 4} fontSize={9} fill="#94a3b8">{Math.round(regionStart / 1000)}k</text>
      <text x={W - 4} y={H - 4} fontSize={9} fill="#94a3b8" textAnchor="end">{Math.round(regionEnd / 1000)}k</text>
    </svg>
  );
}

function VariantListItem({ nv, variant }) {
  const distBp = nv.pos - variant.position;
  const c = pathColor(nv.pathogenicity);
  return (
    <div className="flex items-center gap-3 px-3 py-2.5 rounded-lg border border-gray-100 hover:border-gray-200 bg-white text-xs">
      <div className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: c }} />
      <span className="font-mono font-semibold text-gray-800 w-28 shrink-0">{nv.id}</span>
      <span className="text-gray-500 w-24 shrink-0">{nv.consequence.replace(/_/g, ' ')}</span>
      <span className="font-mono text-gray-600 w-28 shrink-0">{nv.pos.toLocaleString()}</span>
      <span className="text-gray-500">{distBp > 0 ? '+' : ''}{distBp.toLocaleString()} bp</span>
      <span className="font-mono ml-auto" style={{ color: c }}>{nv.pathogenicity.toFixed(2)}</span>
    </div>
  );
}

export default function GenomeBrowser({ variant }) {
  const [zoom, setZoom] = useState(1);
  const [offset, setOffset] = useState(0);

  const handleZoomIn = () => setZoom((z) => Math.min(z * 2, 32));
  const handleZoomOut = () => setZoom((z) => Math.max(z / 2, 0.25));

  const regionSize = Math.round(20000 / zoom);
  const regionStart = variant.position - regionSize / 2 + offset;
  const regionEnd = regionStart + regionSize;

  return (
    <section id="genome" className="bg-white rounded-2xl shadow-card overflow-hidden">
      <div className="px-6 py-4 border-b flex items-center justify-between" style={{ background: '#021f30' }}>
        <div className="flex items-center gap-2">
          <MapPin size={16} className="text-white/70" />
          <h3 className="text-white font-bold text-base">Genome Browser</h3>
        </div>
        <div className="flex items-center gap-2 text-white/60 text-xs">
          <span className="font-mono">chr{variant.chromosome}:{variant.position.toLocaleString()}</span>
        </div>
      </div>

      <div className="p-6 flex flex-col gap-5">
        {/* Chromosome ideogram */}
        <ChromIdeogram chrom={variant.chromosome} variantPos={variant.position} />

        {/* Controls */}
        <div className="flex items-center gap-3">
          <button onClick={handleZoomOut} disabled={zoom <= 0.25}
            className="p-2 rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-40 transition-all">
            <ZoomOut size={16} className="text-gray-600" />
          </button>
          <button onClick={handleZoomIn} disabled={zoom >= 32}
            className="p-2 rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-40 transition-all">
            <ZoomIn size={16} className="text-gray-600" />
          </button>
          <span className="text-sm text-gray-500">Zoom {zoom}x</span>
          <span className="text-xs text-gray-400 ml-2 font-mono">
            {Math.round(regionStart / 1000)}k – {Math.round(regionEnd / 1000)}k ({(regionSize / 1000).toFixed(1)} kb)
          </span>
          <div className="flex items-center gap-1.5 ml-auto text-xs text-gray-400">
            <div className="flex items-center gap-1"><div className="w-2 h-2 rounded-full bg-red-500" /> High</div>
            <div className="flex items-center gap-1"><div className="w-2 h-2 rounded-full bg-amber-500" /> Moderate</div>
            <div className="flex items-center gap-1"><div className="w-2 h-2 rounded-full bg-green-500" /> Low</div>
          </div>
        </div>

        {/* Pan controls */}
        <div className="flex gap-2 -mt-2">
          <button onClick={() => setOffset((o) => o - regionSize * 0.3)}
            className="px-3 py-1.5 text-xs rounded-lg border border-gray-200 hover:bg-gray-50 text-gray-600 transition-all">
            ← Pan Left
          </button>
          <button onClick={() => setOffset(0)}
            className="px-3 py-1.5 text-xs rounded-lg border border-gray-200 hover:bg-gray-50 text-gray-600 transition-all">
            Reset
          </button>
          <button onClick={() => setOffset((o) => o + regionSize * 0.3)}
            className="px-3 py-1.5 text-xs rounded-lg border border-gray-200 hover:bg-gray-50 text-gray-600 transition-all">
            Pan Right →
          </button>
        </div>

        {/* Gene track */}
        <div>
          <div className="text-xs font-semibold text-gray-500 mb-2 uppercase tracking-wide">
            BRCA1 Gene Track — Exons ({variant.exons.length} shown)
          </div>
          <GeneTrackSVG variant={variant} zoom={zoom} offset={offset} />
          <div className="flex items-center gap-4 mt-2 text-xs text-gray-400">
            <div className="flex items-center gap-1.5">
              <div className="w-8 h-3 rounded" style={{ background: '#021f30', opacity: 0.8 }} /> Exon
            </div>
            <div className="flex items-center gap-1.5">
              <div className="w-0.5 h-4 bg-red-500" style={{ borderStyle: 'dashed' }} />
              <span style={{ color: '#f93441' }}>Variant</span>
            </div>
            <div className="flex items-center gap-1.5">
              <div className="w-2.5 h-2.5 rounded-full bg-amber-500" />
              Nearby variants
            </div>
          </div>
        </div>

        {/* Nearby variant list */}
        <div>
          <h4 className="text-sm font-semibold text-gray-700 mb-2">Nearby Variants</h4>
          <div className="flex flex-col gap-1.5">
            <div className="flex items-center gap-3 px-3 py-1.5 text-xs font-semibold text-gray-400 uppercase tracking-wide">
              <div className="w-2.5 h-2.5 shrink-0" />
              <span className="w-28 shrink-0">rsID</span>
              <span className="w-24 shrink-0">Consequence</span>
              <span className="w-28 shrink-0">Position</span>
              <span>Distance</span>
              <span className="ml-auto">Path. Score</span>
            </div>
            {variant.nearby_variants
              .slice()
              .sort((a, b) => Math.abs(a.pos - variant.position) - Math.abs(b.pos - variant.position))
              .map((nv) => (
                <VariantListItem key={nv.id} nv={nv} variant={variant} />
              ))}
          </div>
        </div>
      </div>
    </section>
  );
}
